import { supabase } from '@/integrations/supabase/client';

/** Logical system-audio prompts stored in the private GEN_AUDIOS bucket. */
export type GenAudioKind =
  | 'location_confirmation'
  | 'tap_here_help'
  | 'increase_score_take_pic'
  | 'farm_advisor_welcome'
  | 'onfarm_note'
  // Live LA object names the welcome file with a SPACE ("LA_farm advisor_welcome.mp3").
  // Kept as a fallback candidate so playback works until it's re-uploaded with an
  // underscore; the screen-entry helper prefers the underscore name first.
  | 'farm advisor_welcome';

const BUCKET = 'GEN_AUDIOS';
const SIGNED_URL_TTL_SECONDS = 3600;

/**
 * Resolve a short-lived SIGNED URL for a COUNTRY-CODE-keyed system audio prompt.
 *
 * Objects live at  GEN_AUDIOS/{CODE}/{CODE}_{kind}.mp3  where CODE is the farmer's
 * country code UPPERCASED (e.g. Laos = 'LA') — the folder AND filename use the
 * country code, NOT the language code.
 *
 * Private bucket: signing requires a valid farmer session (RLS). This helper is
 * strictly additive — it returns null (never throws) if the country code is
 * missing, the object is absent, or signing fails, so the calling UI flow is
 * never blocked when audio is unavailable.
 */
export async function resolveGenAudio(
  kind: GenAudioKind,
  countryCode: string | null | undefined,
): Promise<string | null> {
  const code = (countryCode ?? '').trim().toUpperCase();
  if (!code) return null;
  try {
    const path = `${code}/${code}_${kind}.mp3`;
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
}

/**
 * Camera category-tab tabkey → uploaded GEN_AUDIOS filename stem, for the cases
 * where the live object name differs from the tab's value key. Surfaced live:
 * the Laos "fertilizer" tab's note was uploaded as `..._cam_fertilizing.mp3(.mp3)`.
 * The "disease" tab intentionally shares the "pests" note (one recorded file).
 */
const CAMERA_NOTE_ALIASES: Record<string, string> = {
  fertilizer: 'fertilizing',
  disease: 'pests',
};

/**
 * Resolve a short-lived SIGNED URL for a camera category-tab voice note.
 *
 * Objects live at  GEN_AUDIOS/{CODE}/{CODE}_cam_{tabkey}.mp3  (country-code keyed,
 * same private bucket + signing idiom as {@link resolveGenAudio}).
 *
 * Self-healing filename handling: the Laos files were uploaded with a DOUBLE
 * extension (`..._cam_{key}.mp3.mp3`). This helper prefers the convention-correct
 * single-`.mp3` name and falls back to the double-`.mp3.mp3` name, so playback
 * works today AND automatically switches to the clean name once the objects are
 * re-uploaded. Strictly additive: returns null (never throws) when the country
 * code is missing, no object matches, or signing fails — the camera flow is never
 * blocked when a note is unavailable.
 */
export async function resolveCameraNote(
  tabkey: string,
  countryCode: string | null | undefined,
): Promise<string | null> {
  const code = (countryCode ?? '').trim().toUpperCase();
  if (!code || !tabkey) return null;
  const fileKey = CAMERA_NOTE_ALIASES[tabkey] ?? tabkey;
  const candidates = [
    `${code}/${code}_cam_${fileKey}.mp3`,
    `${code}/${code}_cam_${fileKey}.mp3.mp3`,
  ];
  for (const path of candidates) {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
      if (!error && data?.signedUrl) return data.signedUrl;
    } catch {
      /* try next candidate */
    }
  }
  return null;
}

/**
 * STABLE (extension-less) cache key for a camera tab's note object, e.g.
 * `LA/LA_cam_pests`. Applies the same alias map as {@link resolveCameraNote}, so
 * aliased tabs share one cache entry (disease → pests → the same blob) and the key
 * is independent of the .mp3 vs .mp3.mp3 upload reality (a future clean re-upload
 * never orphans the cache). Returns null when the country code / tabkey is missing.
 */
export function cameraNoteStableKey(
  tabkey: string,
  countryCode: string | null | undefined,
): string | null {
  const code = (countryCode ?? '').trim().toUpperCase();
  if (!code || !tabkey) return null;
  const fileKey = CAMERA_NOTE_ALIASES[tabkey] ?? tabkey;
  return `${code}/${code}_cam_${fileKey}`;
}

/**
 * STABLE (extension-less) cache key for a system GEN_AUDIOS clip, e.g.
 * `LA/LA_tap_here_help`. Canonicalises any space-named variant to the underscore
 * form (`farm advisor_welcome` → `LA/LA_farm_advisor_welcome`) so the different
 * resolve candidates map to ONE cache entry. Returns null when code is missing.
 */
export function genAudioStableKey(
  kind: GenAudioKind,
  countryCode: string | null | undefined,
): string | null {
  const code = (countryCode ?? '').trim().toUpperCase();
  if (!code) return null;
  const canonical = kind.replace(/ /g, '_');
  return `${code}/${code}_${canonical}`;
}
